<script setup lang="ts">
/* import */
import { computed } from 'vue'
import type { SvgIconName } from './SvgIcon.types'

/* type */
type SizeType = 'sm' | 'md' | 'lg' | 'xl'
type ColorType = 'primary' | 'secondary' | 'tertiary' | 'white' | 'gray' | 'failure'

/* props(defineProps) */
const props = withDefaults(
  defineProps<{
    iconName: SvgIconName | (string & {})
    size?: SizeType
    color?: ColorType | string
    hoverable?: boolean
  }>(),
  {
    size: 'md',
    color: 'primary',
    hoverable: false
  }
)

/* 非響應式變數 */
// 模組層級快取 getComputedStyle，避免每個 SvgIcon 實例都觸發 forced reflow
let _cachedRootStyles: CSSStyleDeclaration | null = null
const getRootStyles = () => {
  if (!_cachedRootStyles) {
    _cachedRootStyles = getComputedStyle(document.documentElement)
  }
  return _cachedRootStyles
}

/* computed */
const iconName = computed(() => `#icon-${props.iconName}`)
const classes = computed(() => {
  return {
    [`icon-${props.iconName}`]: props.iconName,
    [props.size]: props.size,
    [`color-${props.color}`]: true,
    hoverable: props.hoverable
  }
})
const color = computed(() => {
  const rootStyles = getRootStyles()
  switch (props.color) {
    case 'primary':
      return rootStyles.getPropertyValue('--color-primary').trim()
    case 'secondary':
      return rootStyles.getPropertyValue('--color-secondary').trim()
    case 'tertiary':
      return rootStyles.getPropertyValue('--color-tertiary').trim()
    case 'white':
      return rootStyles.getPropertyValue('--color-white').trim()
    case 'gray':
      return rootStyles.getPropertyValue('--color-gray-600').trim()
    case 'failure':
      return rootStyles.getPropertyValue('--color-failure').trim()
    default:
      return props.color
  }
})
</script>

<template>
  <svg
    class="svg-icon"
    :class="classes"
    v-bind="$attrs"
  >
    <use :href="iconName" />
  </svg>
</template>

<style lang="scss" scoped>
.svg-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  color: v-bind('color');
  pointer-events: none;
  transition: background-color 0.2s ease;

  &.hoverable {
    pointer-events: auto;
    padding: 4px;
    border-radius: $--border-radius-large;

    &.color-primary:hover {
      background-color: rgba($--color-primary, 0.15);
    }
    &.color-secondary:hover {
      background-color: rgba($--color-secondary, 0.15);
    }
    &.color-tertiary:hover {
      background-color: rgba($--color-tertiary, 0.15);
    }
    &.color-white:hover {
      background-color: rgba($--color-white, 0.15);
    }
    &.color-gray:hover {
      background-color: rgba($--color-gray-600, 0.15);
    }
    &.color-failure:hover {
      background-color: rgba($--color-failure, 0.15);
    }
  }

  &.sm {
    width: $--icon-width-sm;
    height: $--icon-height-sm;
  }

  &.md {
    width: $--icon-width-md;
    height: $--icon-height-md;
  }

  &.lg {
    width: $--icon-width-lg;
    height: $--icon-height-lg;
  }

  &.xl {
    width: $--icon-width-xl;
    height: $--icon-height-xl;
  }

  &.logo {
    width: 100%;
    height: 40px;
  }
}
</style>
